import { Component, ElementRef, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { ApiCollection, AuthenticationService, ModalService, Model, strReplace, ToastService, UserModel } from '@kaiser-software/chassis-core';

import { CommentModel } from '../../models/comment.model';
import { CommentService } from '../../services/comment.service';
import { RelationCommentService } from '../../services/relation-comment.service';
import * as pluralize from 'pluralize';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
    selector: 'chassis-comments-comment-relation-plugin',
    templateUrl: './comment-relation.plugin.html',
    styleUrls: ['./comment-relation.plugin.scss'],
    providers: [RelationCommentService]
})
/**
 * The component to represent the comment relations
 */
export class CommentRelationPlugin implements OnInit, OnDestroy {
    @ViewChild('panel') panel: ElementRef;

    /**
     * The form handler which processes the main form
     */
    @Input() form; // form handler which processes the main form

    /**
     * The model subject to subscribe on in order to check whether the product is loaded
     */
    @Input() public modelSubject;

    /**
     * The AuthenticationService instance
     */
    protected authenticationService;

    /**
     * The CommentService instance
     */
    protected commentService: CommentService;

    /**
     * The ModalService instance.
     */
    protected modalService;

    /**
     * The ToastService instance.
     */
    protected toastService: ToastService;

    /**
     * The RelationCommentService instance.
     */
    protected relationCommentService;

    /**
     * The model of the product
     */
    protected model;

    /**
     * The comments
     *
     * @type {ApiCollection}
     */
    protected comments: ApiCollection;

    /**
     * The comment
     */
    protected comment: CommentModel;

    /**
     * The plural form of the url for the addressable service
     *
     * @type {string}
     */
    private serviceModelUrl = '';

    /**
     * The model id of the url for the addressable service
     *
     * @type {string}
     */
    private serviceModelId = '';

    /**
     * The authenticated user instance.
     */
    private authUser;

    /**
     * The settings of the WYSIWYG editor
     *
     * @type {{toolbar: [(string | (string | string | string | string)[])[] , (string | (string | string | string)[])[]]; hint: {mentions: [string , string , string]; match: RegExp; search: ((keyword, callback) => any); content: ((item) => HTMLElement)}}}
     */
    public wysiwygSettings = {
        toolbar: [
            ['style', ['bold', 'italic', 'underline', 'clear']],
            ['font', ['strikethrough', 'superscript', 'subscript']]
        ],
        disableDragAndDrop: true,
        // hint: {
        //     mentions: ['rick', 'bjorn', 'rob'],
        //     match: /\B@(\w*)$/,
        //     search: function (keyword, callback) {
        //         callback($.grep(this.mentions, (item: string) => {
        //             return item.indexOf(keyword) === 0;
        //         }));
        //     },
        //     content: function (item) {
        //         return $('<span class="text-complete">@' + item + '</span>')[0];
        //     }
        // }
    };

    /**
     * The loading indicator
     * @type {boolean}
     */
    protected loading = true;

    protected subscription = new Subscription();

    constructor(protected injector: Injector) {
        this.authenticationService  = this.injector.get(AuthenticationService);
        this.commentService         = this.injector.get(CommentService);
        this.modalService           = this.injector.get(ModalService);
        this.toastService           = this.injector.get(ToastService);
        this.relationCommentService = this.injector.get(RelationCommentService);
    }

    ngOnInit() {
        this.comment = this.commentService.make();

        this.setupServiceVariables();

        this.subscription.add(
            this.modelSubject.subscribe((model: Model) => {
                this.loading = true;

                this.model = model;

                if (model.exists()) {
                    this.relationCommentService.all().subscribe(comments => {
                        this.comments = comments;

                        this.loading = false;
                    });
                } else {
                    this.setupProcessingCallback();

                    this.subscription.add(
                        this.authenticationService.user()
                            .pipe(
                                take(1),
                            )
                            .subscribe((user: UserModel) => {
                                this.authUser = user;

                            }, (error) => {
                                console.log(error);

                                this.toastService.error('error', 'Authenticated user can\'t be fetched', {element: this.panel.nativeElement});
                            })
                    );

                    this.comments = new ApiCollection([], new CommentModel, this.relationCommentService);

                    this.loading = false;
                }
            })
        );
    }

    public addComment(): void {
        const comment = this.comment.plain();

        if (this.model.exists() === false) {
            // Add user data to ensure the avatar and name are displayed properly
            this.addUserData(comment);

            this.comments.push(comment);

            this.toastService.info('info', 'Comment added successfully', {element: this.panel.nativeElement});

            // Reset the comment
            this.comment = this.commentService.make();
        } else {
            this.loading = true;

            this.comments.post(comment)
                .subscribe((savedComment: CommentModel) => {

                    this.loading = false;

                    this.toastService.success('success', 'Comment saved successfully', {element: this.panel.nativeElement});

                    // Reset the comment
                    this.comment = this.commentService.make();
                }, error => {
                    this.loading = false;

                    this.toastService.error('error', 'Something went wrong while saving the comment', {element: this.panel.nativeElement});
                });
        }
    }

    /**
     * Edit a comment
     *
     * @param {CommentModel} model
     */
    public editComment(model: CommentModel): void {
        // TODO: make a edit view(modal)?
    }

    /**
     * Delete a comment
     *
     * @param {CommentModel} model
     */
    public deleteComment(model: CommentModel): void {
        if (this.model.exists() === false) {
            this.comments.forget('id', model.id);

            this.toastService.info('info', 'Comment removed successfully', {element: this.panel.nativeElement});
        } else {
            this.comments.delete(model.id).subscribe((deletedComment: CommentModel) => {
                // console.log('Deleted: ', deletedComment);

                this.loading = false;

                this.toastService.success('success', 'Comment deleted successfully', {element: this.panel.nativeElement});
            }, (error) => {
                console.error(error);

                this.loading = false;

                this.toastService.error('error', 'Failed to delete comment', {element: this.panel.nativeElement});
            });
        }
    }

    /**
     * Add user data to show avatar and name in the create phase
     */
    protected addUserData(comment): void {
        comment.user        = {};
        comment.user.name   = this.authUser.name;
        comment.user.avatar = this.authUser.avatar;
    }

    /**
     * Process data with input from the model coming from the main form handler
     */
    public setupProcessingCallback(): void {
        if (this.form) {
            this.form.success((model) => {
                return new Promise((resolve, reject) => {
                    if (this.comments && this.comments.count() > 0) {
                        // Set the id for the service url
                        this.serviceModelId = model.id;
                        this.setServiceVariables();

                        this.comments.forEach((comment) => {
                            this.loading = true;

                            this.relationCommentService.post(comment.plain()).subscribe((savedComment: CommentModel) => {
                                // console.log('Saved: ', savedComment);

                                this.loading = false;

                                this.toastService.success('success', 'Comment saved successfully', {element: this.panel.nativeElement});

                                resolve();
                            }, (error) => {
                                console.error('Error: ', error);

                                this.loading = false;

                                this.toastService.error('error', 'Failed to save comment', {element: this.panel.nativeElement});

                                reject();
                            });
                        });
                    } else {
                        resolve();
                    }
                });
            });
        } else {
            console.error('There is no form handler supplied.');
        }
    }

    /**
     * Get the current route url segments
     */
    private setupServiceVariables() {
        this.subscription.add(
            this.modelSubject.subscribe((model: Model) => {
                if (model.id && model.id > 0) {
                    this.serviceModelId = model.id + '';
                }

                this.serviceModelUrl = pluralize(strReplace(model._service.entityName, '_', '-'));
                this.setServiceVariables();
            })
        );
    }

    /**
     * Set the universal service variables
     */
    private setServiceVariables(): void {
        this.relationCommentService.defaultParams = {pluralModel: this.serviceModelUrl, idModel: this.serviceModelId};
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-comments/src/app/plugins/comment-relation/comment-relation.plugin.ts